import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileDown, Upload, FileText, Receipt, Award, FileSignature, Package } from "lucide-react";

const CATEGORIES = [
  { value: "fattura", label: "Pro-forma", icon: Receipt, color: "bg-blue-100 text-blue-700" },
  { value: "attestato", label: "Attestato", icon: Award, color: "bg-emerald-100 text-emerald-700" },
  { value: "contratto", label: "Contratto", icon: FileSignature, color: "bg-amber-100 text-amber-700" },
  { value: "altro", label: "Altro", icon: Package, color: "bg-slate-100 text-slate-700" },
];
const catMeta = (c) => CATEGORIES.find((x) => x.value === c) || CATEGORIES[3];

export default function PartnerDocuments() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [upCategory, setUpCategory] = useState("altro");
  const [upTitle, setUpTitle] = useState("");

  const load = useCallback(() => {
    api.get("/documents", { params: cat !== "all" ? { category: cat } : {} }).then(r => setDocs(r.data));
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/documents/upload", fd, {
        params: { partner_id: user.partner_id, category: upCategory, title: upTitle || file.name },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato");
      setUpTitle(""); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const sizeKb = (b) => `${(b / 1024).toFixed(1)} KB`;

  return (
    <div className="space-y-6" data-testid="partner-documents">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Area documentale</h1>
        <p className="text-slate-500 mt-1">Fatture, attestati, contratti e documentazione corsi.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Titolo documento</Label>
              <Input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="Es. Pro-forma 2026-001" data-testid="doc-upload-title" />
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select value={upCategory} onValueChange={setUpCategory}>
                <SelectTrigger className="w-44" data-testid="doc-upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="self-end">
              <input type="file" className="hidden" onChange={handleUpload} data-testid="doc-upload-input" />
              <Button asChild disabled={uploading} className="bg-[#0F2942] hover:bg-[#1B3B5F]">
                <span><Upload className="w-4 h-4 mr-1" /> {uploading ? "Caricamento…" : "Carica documento"}</span>
              </Button>
            </label>
          </div>
          <div className="text-xs text-slate-400 mt-2">Formati supportati: PDF, immagini, Office · max 20MB</div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant={cat === "all" ? "default" : "outline"} onClick={() => setCat("all")} size="sm" data-testid="doc-filter-all">Tutti</Button>
        {CATEGORIES.map((c) => (
          <Button key={c.value} variant={cat === c.value ? "default" : "outline"} onClick={() => setCat(c.value)} size="sm" data-testid={`doc-filter-${c.value}`}>
            <c.icon className="w-3 h-3 mr-1" /> {c.label}
          </Button>
        ))}
      </div>

      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Titolo</TableHead><TableHead>Categoria</TableHead>
            <TableHead>File</TableHead><TableHead>Dim.</TableHead>
            <TableHead>Caricato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.map((d) => {
              const m = catMeta(d.category);
              return (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-medium">{d.title}</TableCell>
                  <TableCell><span className={`status-pill ${m.color}`}><m.icon className="w-3 h-3" />{m.label}</span></TableCell>
                  <TableCell className="text-xs text-slate-500 truncate max-w-[200px]">{d.original_filename}</TableCell>
                  <TableCell className="text-xs">{sizeKb(d.size || 0)}</TableCell>
                  <TableCell className="text-xs">{dateIt(d.created_at)}</TableCell>
                  <TableCell>
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" data-testid={`doc-download-${d.id}`}><FileDown className="w-4 h-4 mr-1" />Scarica</Button>
                    </a>
                  </TableCell>
                </TableRow>
              );
            })}
            {docs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12">
                <FileText className="w-10 h-10 mx-auto text-slate-300" />
                <div className="mt-2">Nessun documento in questa categoria.</div>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
